import { useEffect } from 'react';

import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { PresentationTemplateType } from '@/types/schema';

import { usePresentationTemplateQuery } from './graphql/PresentationTemplateEditor.generated';
import {
  DEFAULT_TEMPLATE_NAME,
  NEW_TEMPLATE_SENTINEL,
} from './PresentationTemplateEditor.constants';
import {
  PresentationTemplateEditorContextProvider,
  usePresentationTemplateEditorContext,
} from './PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorFormShape,
  PresentationTemplateEditorStage,
} from './PresentationTemplateEditor.types';
import { mapPresentationTemplateToFormShape } from './PresentationTemplateEditor.utils';

export interface PresentationTemplateEditorProps {
  templateId: string;
  onClose: () => void;
  /**
   * Optional callback invoked after a successful save (create or update).
   * When provided, this is called instead of onClose after saving.
   */
  onSaveComplete?: () => void;
  /**
   * When true, the loaded template is treated as a read-only source: the editor
   * only offers "Save as new template" (which creates a new template of
   * `createTemplateType`) instead of updating in place. Used when opening a
   * template the current context can't edit directly — a firm template from the
   * client list, or a system template (e.g. Estate360).
   */
  isDuplicateFlow?: boolean;
  /**
   * The template type to use when creating a new template (via "save as new" or
   * the create flow). Determined by which page the editor is opened from:
   * admin page → TenantTemplate, user page → UserTemplate.
   */
  createTemplateType: PresentationTemplateType;
}

export function PresentationTemplateEditor({
  templateId,
  onClose,
  onSaveComplete,
  isDuplicateFlow = false,
  createTemplateType,
}: PresentationTemplateEditorProps) {
  const isCreating = templateId === NEW_TEMPLATE_SENTINEL;

  // disableEnforceFocus is required because this modal contains
  // SlideEditorTitleTextEditor, which uses a contenteditable element
  // that conflicts with MUI's focus trap polling.
  return (
    <FullScreenModal
      trowserKey={TrowserKey.Standalone}
      isOpen
      onClose={onClose}
      disableEnforceFocus
    >
      <PresentationTemplateEditorContextProvider
        onClose={onClose}
        onSaveComplete={onSaveComplete}
        isDuplicateFlow={isDuplicateFlow}
        isCreating={isCreating}
        createTemplateType={createTemplateType}
      >
        {isCreating ? (
          <NewPresentationTemplateEditorInner />
        ) : (
          <ExistingPresentationTemplateEditorInner templateId={templateId} />
        )}
      </PresentationTemplateEditorContextProvider>
    </FullScreenModal>
  );
}

function NewPresentationTemplateEditorInner() {
  const { reset } = useFormContext<PresentationTemplateEditorFormShape>();
  const { setStage, body } = usePresentationTemplateEditorContext();

  useEffect(() => {
    reset({
      id: NEW_TEMPLATE_SENTINEL,
      displayName: DEFAULT_TEMPLATE_NAME,
      bundles: [],
      isSystemTemplate: false,
    });
    setStage(PresentationTemplateEditorStage.Template);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on mount
  }, []);

  return body;
}

interface ExistingPresentationTemplateEditorInnerProps {
  templateId: string;
}
function ExistingPresentationTemplateEditorInner({
  templateId,
}: ExistingPresentationTemplateEditorInnerProps) {
  const { id: tenantId } = useTenantDetailsContext();
  const { reset } = useFormContext<PresentationTemplateEditorFormShape>();
  const { setStage, body } = usePresentationTemplateEditorContext();

  // System templates are excluded by the query until T1-5620 adds edit support.
  const { error } = usePresentationTemplateQuery({
    fetchPolicy: 'network-only',
    variables: {
      templateId,
    },
    onCompleted: (data) => {
      const formData = mapPresentationTemplateToFormShape(data, tenantId);
      if (!formData) {
        setStage(PresentationTemplateEditorStage.Error);
        return;
      }
      reset(formData);
      setStage(PresentationTemplateEditorStage.Template);
    },
  });

  useReportError('Presentation template editor', error, {
    templateId,
  });

  return body;
}
